import React, { useMemo } from 'react';
import { FileQuestion, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import {
  REQUEST_TYPES,
  SERVICE_STATUS,
  PRIORITY_OPTIONS,
  statusBadge,
  defaultStats,
  countByNormalizedStatus,
  normalizeServiceStatus,
  normalizePriority,
  normalizeRequestType,
  getRequestTypeLabel,
} from './employeePortalShared';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'serviceRequestsPage';
const API = '/api/employee-portal/service-requests';

const EMPTY = {
  request_code: '', request_type: '', title: '', description: '', status: 'submitted', priority: 'normal', notes: '',
};

/** Map Saudi portal status to DB CHECK constraint values. */
function statusToDb(status) {
  const map = {
    submitted: 'nouveau',
    pending_manager: 'en_cours',
    pending_hr: 'en_cours',
    in_progress: 'en_cours',
    completed: 'traite',
    rejected: 'rejete',
    cancelled: 'annule',
  };
  return map[status] || status;
}

function priorityToDb(priority) {
  const map = { low: 'basse', high: 'haute', urgent: 'urgente' };
  return map[priority] || priority;
}

export default function EmployeePortalServiceRequestsPage() {
  const { employeeId, name, isAdmin } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => (employeeId ? { employee_id: employeeId } : {}), [employeeId]);

  const typeOpts = REQUEST_TYPES.map((t) => ({ value: t.value, labelKey: t.labelKey }));
  const statusOpts = SERVICE_STATUS.map((s) => ({ value: s.value, labelKey: s.labelKey }));
  const priorityOpts = PRIORITY_OPTIONS.map((p) => ({ value: p.value, labelKey: p.labelKey }));

  const formSections = useMemo(() => {
    const requestFields = [
      { key: 'title', labelKey: 'field_title', required: true },
      { key: 'request_type', labelKey: 'field_type', type: 'select', options: typeOpts, required: true },
      { key: 'priority', labelKey: 'field_priority', type: 'select', options: priorityOpts },
    ];
    if (isAdmin) {
      requestFields.push({ key: 'status', labelKey: 'field_status', type: 'select', options: statusOpts });
    }
    return [
      {
        id: 'request',
        titleKey: 'section_request',
        fields: requestFields,
      },
      {
        id: 'details',
        titleKey: 'section_details',
        fields: [
          { key: 'description', labelKey: 'field_description', type: 'textarea' },
          { key: 'notes', labelKey: 'field_notes', type: 'textarea' },
        ],
      },
    ];
  }, [isAdmin, typeOpts, priorityOpts, statusOpts]);

  return (
    <EmployeePortalCrudShell
      ns={NS}
      icon={FileQuestion}
      apiPath={API}
      apiQueryParams={apiQueryParams}
      requireEmployeeScope
      codeField="request_code"
      entityPrefixKey="employeePortalServiceRequest"
      codePrefixFallback="EPSR"
      allowCreate
      allowEdit={isAdmin}
      allowDelete={isAdmin}
      gradient="from-cyan-500 to-cyan-700"
      emptyForm={EMPTY}
      normalizeStatus={normalizeServiceStatus}
      mapStatusToPayload={(status) => ({ status: statusToDb(status) })}
      mapRecordToForm={(r) => ({
        request_code: r.request_code ?? '',
        request_type: normalizeRequestType(r.request_type) ?? '',
        title: r.title ?? '',
        description: r.description ?? '',
        status: normalizeServiceStatus(r.status) ?? 'submitted',
        priority: normalizePriority(r.priority) ?? 'normal',
        notes: r.notes ?? '',
      })}
      mapFormToPayload={(f, editing) => ({
        request_code: f.request_code || null,
        request_type: f.request_type || null,
        title: f.title?.trim(),
        description: f.description?.trim() || null,
        status: statusToDb(isAdmin && f.status ? f.status : (editing ? normalizeServiceStatus(editing.status) : 'submitted')),
        priority: priorityToDb(f.priority || 'normal'),
        notes: f.notes?.trim() || null,
        employee_id: employeeId || null,
        employee_name: name || null,
      })}
      computeStats={(records) => defaultStats(records, {
        totalIcon: FileQuestion,
        fields: [
          {
            titleKey: 'stat_new',
            icon: AlertCircle,
            color: 'from-amber-500 to-amber-600',
            countFn: (recs) => countByNormalizedStatus(recs, 'status', 'submitted', normalizeServiceStatus),
          },
          {
            titleKey: 'stat_in_progress',
            icon: Clock,
            color: 'from-blue-500 to-blue-600',
            countFn: (recs) => recs.filter((r) => ['in_progress', 'pending_manager', 'pending_hr'].includes(normalizeServiceStatus(r.status))).length,
          },
          {
            titleKey: 'stat_done',
            icon: CheckCircle,
            color: 'from-green-500 to-green-600',
            countFn: (recs) => countByNormalizedStatus(recs, 'status', 'completed', normalizeServiceStatus),
          },
          {
            titleKey: 'stat_rejected',
            icon: XCircle,
            color: 'from-red-500 to-red-600',
            countFn: (recs) => countByNormalizedStatus(recs, 'status', 'rejected', normalizeServiceStatus),
          },
        ],
      })}
      columns={[
        { key: 'request_code', labelKey: 'col_code' },
        { key: 'title', labelKey: 'col_title' },
        { key: 'request_type', labelKey: 'col_type', render: (r, _p, _t, pl) => getRequestTypeLabel(r.request_type, pl) },
        { key: 'priority', labelKey: 'col_priority', render: (r, _p, _t, pl) => {
          const opt = PRIORITY_OPTIONS.find((o) => o.value === normalizePriority(r.priority));
          return opt ? pl(opt.labelKey) : r.priority;
        }},
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl, SERVICE_STATUS) },
      ]}
      formSections={formSections}
    />
  );
}
